/**
 * SSE consumer for POST /api/nl-search.
 *
 * Returns an async iterator yielding one event at a time. The server emits:
 *   filters  → KeepaFilters
 *   progress → { stage }
 *   result   → NlSearchResponse
 *   error    → { code, message }
 *
 * We use fetch + a ReadableStream rather than EventSource because EventSource
 * only supports GET; we want to POST a JSON body with the query/filters.
 */
import {
  NlSearchPreviewResponse,
  type KeepaFilters,
  type NlSearchPreviewRequest,
  type NlSearchRequest,
  type NlSearchResponse,
} from '@sourcito/schemas';
import { apiFetch } from './api';

export type NlSearchEvent =
  | { event: 'filters'; data: KeepaFilters }
  | { event: 'progress'; data: { stage: string } }
  | { event: 'result'; data: NlSearchResponse }
  | { event: 'error'; data: { code: string; message: string } };

/**
 * Make a streaming nl-search request. Yields events as they arrive.
 * Throws if the initial HTTP response is non-OK (pre-flight checks fail
 * with JSON, not SSE).
 */
export async function* streamNlSearch(
  body: NlSearchRequest,
  options: { signal?: AbortSignal } = {}
): AsyncGenerator<NlSearchEvent> {
  const res = await fetch('/api/nl-search', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal: options.signal,
  });

  if (!res.ok) {
    let message = `nl-search returned HTTP ${res.status}`;
    let code = 'HTTP_ERROR';
    try {
      const err = (await res.json()) as { error?: { code?: string; message?: string } };
      if (err.error?.message) message = err.error.message;
      if (err.error?.code) code = err.error.code;
    } catch {
      /* not JSON */
    }
    yield { event: 'error', data: { code, message } };
    return;
  }

  if (!res.body) {
    yield { event: 'error', data: { code: 'NO_STREAM', message: 'Response had no body' } };
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE blocks are separated by \n\n
      let blockEnd: number;
      while ((blockEnd = buffer.indexOf('\n\n')) !== -1) {
        const block = buffer.slice(0, blockEnd);
        buffer = buffer.slice(blockEnd + 2);
        const parsed = parseSSEBlock(block);
        if (parsed) yield parsed;
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * One-shot translation + token estimate. Hits POST /api/nl-search/preview;
 * does NOT call Keepa. Cheap (sub-cent LLM call), returns instantly.
 */
export async function previewNlSearch(
  body: NlSearchPreviewRequest
): Promise<NlSearchPreviewResponse> {
  return apiFetch<NlSearchPreviewResponse>('/api/nl-search/preview', {
    method: 'POST',
    body: JSON.stringify(body),
    responseSchema: NlSearchPreviewResponse,
  });
}

/**
 * Cache-only brand-size lookup. Used by SearchPage to poll for badge
 * hydration after the SSE stream closes with `pending` brands. No Keepa
 * cost — server reads keepa_brand_cache and returns the current bucket
 * per brand (or `'pending'` for cache-miss / failed-cooldown).
 */
export async function fetchBrandSizes(
  names: string[]
): Promise<Record<string, 'micro' | 'small' | 'medium' | 'large' | 'pending'>> {
  if (names.length === 0) return {};
  const param = encodeURIComponent(names.join(','));
  const res = await fetch(`/api/brand-sizes?names=${param}`, { credentials: 'include' });
  if (!res.ok) return {};
  const body = (await res.json()) as {
    brands?: Record<string, 'micro' | 'small' | 'medium' | 'large' | 'pending'>;
  };
  return body.brands ?? {};
}

function parseSSEBlock(block: string): NlSearchEvent | null {
  let event: string | null = null;
  let data: string | null = null;
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) data = line.slice(5).trim();
  }
  if (!event || data === null) return null;
  try {
    const parsedData = JSON.parse(data);
    return { event, data: parsedData } as NlSearchEvent;
  } catch {
    return null;
  }
}
